/**
 * Rhythmic Dictation Drill — free-practice mode.
 *
 * Hear a rhythm in meter context; mark the attack positions on the beat grid.
 */

import {
  useReducer,
  useRef,
  useEffect,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CoachSessionDoneCard } from "../components/CoachSessionDone.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import { ModeSwitcherBar, MODE_COLOR as PICKER_MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { ExerciseLessonLink } from "../components/ExerciseLessonLink.js";
import { useListenSession } from "../lib/use-listen-session.js";
import { useExerciseSession } from "../lib/use-exercise-session.js";
import { nextRoundSeed } from "../lib/round-seed.js";
import { ListenAutoAdvanceControl } from "../components/ListenAutoAdvanceControl.js";
import {
  buildRhythmicDictationExerciseRound,
  gradeRhythmicGrid,
  rhythmicDictationRating,
  rhythmicDrillItemId,
  rhythmicDrillStatsKey,
  DEFAULT_RHYTHMIC_DICTATION_EXERCISE_CONFIG,
  type RhythmicDictationExerciseConfig,
  type RhythmicDictationExerciseRound,
  type RhythmicDictationGrading,
  type RhythmicDrillDifficulty,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import {
  useCardsStore,
  usePrefsStore,
  getDrillStats,
  upsertDictationDrillStats,
  recordReview as dbRecordReview,
} from "@music-app/persistence";
import { exerciseReviewSource } from "@music-app/learning-system";
import { RhythmicDictationInput } from "@music-app/ui-components";
import { createUISoundPlayer, type UISoundPlayer } from "../lib/ui-sounds.js";

// ─── State machine ────────────────────────────────────────────────────────────

type Phase = "config" | "playing" | "awaiting" | "graded" | "reveal" | "done";

interface DrillState {
  phase: Phase;
  config: RhythmicDictationExerciseConfig;
  round: RhythmicDictationExerciseRound | null;
  roundNumber: number;
  grid: boolean[];
  grading: RhythmicDictationGrading | null;
  accuracySum: number;
  roundsGraded: number;
}

type DrillAction =
  | { type: "START"; config: RhythmicDictationExerciseConfig; round: RhythmicDictationExerciseRound }
  | { type: "PLAYBACK_DONE" }
  | { type: "CHANGE_GRID"; grid: boolean[] }
  | { type: "SUBMIT"; grading: RhythmicDictationGrading }
  | { type: "NEXT_ROUND"; round: RhythmicDictationExerciseRound }
  | { type: "SESSION_DONE" }
  | { type: "STOP" };

const INITIAL_STATE: DrillState = {
  phase: "config",
  config: DEFAULT_RHYTHMIC_DICTATION_EXERCISE_CONFIG,
  round: null,
  roundNumber: 0,
  grid: [],
  grading: null,
  accuracySum: 0,
  roundsGraded: 0,
};

function reducer(state: DrillState, action: DrillAction): DrillState {
  switch (action.type) {
    case "START":
      return {
        ...INITIAL_STATE,
        phase: "playing",
        config: action.config,
        round: action.round,
        roundNumber: 1,
        grid: emptyGrid(action.round.cellCount),
      };
    case "PLAYBACK_DONE":
      if (state.phase !== "playing") return state;
      return { ...state, phase: state.config.sessionLength === 0 ? "reveal" : "awaiting" };
    case "CHANGE_GRID":
      if (state.phase !== "awaiting") return state;
      return { ...state, grid: action.grid };
    case "SUBMIT":
      if (state.phase !== "awaiting") return state;
      return {
        ...state,
        phase: "graded",
        grading: action.grading,
        accuracySum: state.accuracySum + action.grading.accuracy * 100,
        roundsGraded: state.roundsGraded + 1,
      };
    case "NEXT_ROUND":
      return {
        ...state,
        phase: "playing",
        round: action.round,
        roundNumber: state.roundNumber + 1,
        grid: emptyGrid(action.round.cellCount),
        grading: null,
      };
    case "SESSION_DONE":
      return { ...state, phase: "done" };
    case "STOP":
      return { ...INITIAL_STATE };
  }
}

function emptyGrid(n: number): boolean[] {
  return Array<boolean>(n).fill(false);
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function RhythmicDictationExercisePage(): ReactNode {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const playerRef = useRef<AudioPlayer | null>(null);
  const uiSoundRef = useRef<UISoundPlayer | null>(null);
  const responseStartRef = useRef<number | null>(null);

  const [searchParams] = useSearchParams();
  const VALID_MODES: ExerciseMode[] = ["listen", "practice", "review"];
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: ExerciseMode = rawMode && VALID_MODES.includes(rawMode) ? rawMode : "practice";
  const listenMode = mode === "listen";
  const practiceMode = mode === "review";
  const coached = searchParams.get("coached") === "true";
  const coachItemCount = Math.max(1, parseInt(searchParams.get("itemCount") ?? "10", 10));
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const from = searchParams.get("from") ?? undefined;

  const { recordPlay } = useListenSession("rhythmic-dictation", listenMode, nodeId);
  const { recordItem } = useExerciseSession(
    "rhythmic-dictation",
    practiceMode ? "practice" : "drill",
    !listenMode,
  );

  const applyReview = useCardsStore((s) => s.applyReview);
  const instrument = usePrefsStore((s) => s.instrument);
  const uiSoundsEnabled = usePrefsStore((s) => s.uiSoundsEnabled);

  useEffect(() => {
    const player = createAudioPlayer();
    playerRef.current = player;
    return () => {
      player.stop();
      player.destroy();
      uiSoundRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !instrument) return;
    void (async () => {
      await player.init();
      if (!player.isInstrumentLoaded(instrument)) await player.loadInstrument(instrument);
      player.setInstrument(instrument);
    })();
  }, [instrument]);

  // Persist drill stats after grading
  useEffect(() => {
    if (state.phase !== "graded" || !state.grading || !state.round) return;
    void upsertDictationDrillStats(
      rhythmicDrillStatsKey(state.config),
      Math.round(state.grading.accuracy * 100),
    );
  }, [state.phase, state.round, state.grading, state.config]);

  // Listen mode: record play on reveal
  useEffect(() => {
    if (!listenMode || state.phase !== "reveal") return;
    recordPlay(`${nodeId ?? "rhythmic-dictation-simple"}::${state.roundNumber}`);
  }, [listenMode, state.phase, state.roundNumber, nodeId, recordPlay]);

  const playRound = useCallback(
    async (round: RhythmicDictationExerciseRound) => {
      const player = playerRef.current;
      if (!player) return;
      player.stop();
      await player.init();
      const pref = usePrefsStore.getState().instrument;
      if (!player.isInstrumentLoaded(pref)) await player.loadInstrument(pref);
      player.setInstrument(pref);
      await player.play(round.audioPlan);
      if (listenMode) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 1500));
        await player.play(round.teachAudio);
      }
      recordItem(`${nodeId ?? "rhythmic-dictation-simple"}::${state.roundNumber}`);
      dispatch({ type: "PLAYBACK_DONE" });
    },
    [listenMode, recordItem, nodeId, state.roundNumber],
  );

  const replay = useCallback(() => {
    if (!state.round) return;
    void playerRef.current?.play(state.round.audioPlan);
  }, [state.round]);

  const replayTeach = useCallback(() => {
    if (!state.round) return;
    void playerRef.current?.play(state.round.teachAudio);
  }, [state.round]);

  const startDrill = useCallback(
    (config: RhythmicDictationExerciseConfig) => {
      const round = buildRhythmicDictationExerciseRound(config, nextRoundSeed());
      dispatch({ type: "START", config, round });
      void playRound(round);
    },
    [playRound],
  );

  const handleSubmit = useCallback(() => {
    if (!state.round || state.phase !== "awaiting") return;
    const grading = gradeRhythmicGrid(state.round.params.pattern, state.grid);
    dispatch({ type: "SUBMIT", grading });
    if (uiSoundsEnabled) {
      if (!uiSoundRef.current) uiSoundRef.current = createUISoundPlayer();
      uiSoundRef.current.play(grading.correct ? "correct" : "wrong");
    }
    if (!grading.correct) {
      const plan = state.round.teachAudio;
      window.setTimeout(() => { void playerRef.current?.play(plan); }, 600);
    }
  }, [state.round, state.phase, state.grid, uiSoundsEnabled]);

  const nextRound = useCallback(() => {
    if (!state.round) return;
    if (state.roundNumber >= state.config.sessionLength) {
      dispatch({ type: "SESSION_DONE" });
      return;
    }
    const round = buildRhythmicDictationExerciseRound(state.config, nextRoundSeed());
    dispatch({ type: "NEXT_ROUND", round });
    void playRound(round);
  }, [state.config, state.round, state.roundNumber, playRound]);

  const handleStop = useCallback(() => {
    playerRef.current?.stop();
    dispatch({ type: "STOP" });
  }, []);

  // Record practice review
  useEffect(() => {
    if (state.phase !== "graded" || !state.grading || !state.round) return;
    if (listenMode) return;
    const responseMs = responseStartRef.current ? Date.now() - responseStartRef.current : 8000;
    const itemId = rhythmicDrillItemId(state.config, state.round.params.pattern);
    void dbRecordReview({
      itemId,
      sessionId: "",
      rating: rhythmicDictationRating(state.grading),
      correct: state.grading.correct,
      responseMs,
      chosenAnswerId: null,
      source: exerciseReviewSource({ challengeMode: false, fsrsEnabled: practiceMode }),
    });
    if (practiceMode) {
      void applyReview(itemId, rhythmicDictationRating(state.grading));
    }
  }, [state.phase, state.grading, state.round, state.config, listenMode, practiceMode, applyReview]);

  useEffect(() => {
    if (state.phase === "awaiting") responseStartRef.current = Date.now();
  }, [state.phase]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key.toLowerCase() === "r" && state.phase !== "config" && state.phase !== "done") {
        e.preventDefault();
        replay();
        return;
      }
      if ((state.phase === "graded" || state.phase === "reveal") &&
          (e.key === " " || e.key === "Enter")) {
        e.preventDefault();
        nextRound();
        return;
      }
      if (state.phase === "awaiting" && e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.phase, nextRound, replay, handleSubmit]);

  const modeColor = PICKER_MODE_COLOR[mode];

  if (state.phase === "config") {
    if (coached) {
      return (
        <CoachStart
          nodeId={nodeId}
          itemCount={coachItemCount}
          onStart={startDrill}
        />
      );
    }
    return <ConfigScreen mode={mode} modeColor={modeColor} onStart={startDrill} from={from} nodeId={nodeId} />;
  }

  if (state.phase === "done") {
    if (coached) return <CoachSessionDoneCard />;
    return (
      <Summary
        config={state.config}
        accuracySum={state.accuracySum}
        roundsGraded={state.roundsGraded}
        onAgain={() => startDrill(state.config)}
        onStop={handleStop}
        from={from}
      />
    );
  }

  return (
    <DrillLoop
      state={state}
      onChangeGrid={(g) => dispatch({ type: "CHANGE_GRID", grid: g })}
      onSubmit={handleSubmit}
      onNext={nextRound}
      onReplay={replay}
      onReplayTeach={replayTeach}
      onStop={handleStop}
      listenMode={listenMode}
      practiceMode={practiceMode}
      modeColor={modeColor}
      from={from}
    />
  );
}

// ─── Coach auto-start ──────────────────────────────────────────────────────────

function CoachStart({
  nodeId,
  itemCount,
  onStart,
}: {
  nodeId: string | undefined;
  itemCount: number;
  onStart: (config: RhythmicDictationExerciseConfig) => void;
}): ReactNode {
  useEffect(() => {
    const difficulty: RhythmicDrillDifficulty =
      nodeId === "rhythmic-dictation-syncopation" ? "syncopation" :
      nodeId === "rhythmic-dictation-compound" ? "compound" : "simple";
    onStart({ difficulty, sessionLength: itemCount });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting listen session…</p>
    </div>
  );
}

// ─── Config screen ─────────────────────────────────────────────────────────────

const SESSION_LENGTH_PRESETS = [5, 10, 15, 20, 30];
const DIFFICULTY_OPTIONS: { value: RhythmicDrillDifficulty; label: string; desc: string }[] = [
  { value: "simple",      label: "Simple (4/4)",    desc: "Quarter and eighth note figures, no syncopation." },
  { value: "compound",    label: "Compound (6/8)",  desc: "Compound beat figures, dotted rhythms." },
  { value: "syncopation", label: "Syncopation",     desc: "Off-beat attacks and cross-beat ties." },
];

const LS_KEY = "rhythmic-dictation-drill-config-v1";

interface SavedConfig {
  difficulty: RhythmicDrillDifficulty;
  sessionLength: number;
}

function loadConfig(): SavedConfig {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...{ difficulty: "simple" as const, sessionLength: 10 }, ...(JSON.parse(raw) as Partial<SavedConfig>) };
  } catch { /* ignore */ }
  return { difficulty: "simple", sessionLength: 10 };
}

function saveConfig(cfg: SavedConfig): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

// Preselect the difficulty for the lesson's node when arriving via "Practice
// this" (`?nodeId=…`). Mirrors `CoachStart`.
function rhythmNodeConfigPatch(nodeId: string | undefined): Partial<ReturnType<typeof loadConfig>> {
  if (nodeId === "rhythmic-dictation-compound") return { difficulty: "compound" };
  if (nodeId === "rhythmic-dictation-syncopation") return { difficulty: "syncopation" };
  if (nodeId === "rhythmic-dictation-simple") return { difficulty: "simple" };
  return {};
}

function ConfigScreen({
  mode,
  modeColor,
  onStart,
  from,
  nodeId,
}: {
  mode: ExerciseMode;
  modeColor: string;
  onStart: (config: RhythmicDictationExerciseConfig) => void;
  from?: string;
  nodeId?: string;
}): ReactNode {
  const [initConfig] = useState(() => ({ ...loadConfig(), ...rhythmNodeConfigPatch(nodeId) }));
  const [difficulty, setDifficulty] = useState<RhythmicDrillDifficulty>(initConfig.difficulty);
  const [sessionLength, setSessionLength] = useState(initConfig.sessionLength);
  const [showCustom, setShowCustom] = useState(!SESSION_LENGTH_PRESETS.includes(initConfig.sessionLength));
  const [stats, setStats] = useState<{ played: number; avg: number } | null>(null);

  useEffect(() => {
    saveConfig({ difficulty, sessionLength });
    void getDrillStats(rhythmicDrillStatsKey({ difficulty, sessionLength })).then((s) => {
      if (s && s.played > 0) {
        setStats({ played: s.played, avg: s.accuracySum !== undefined ? Math.round(s.accuracySum / s.played) : 0 });
      } else {
        setStats(null);
      }
    });
  }, [difficulty, sessionLength]);

  const back = from === "learn"
    ? { label: "← Learn", href: "/learn" }
    : { label: "← Ear Training", href: "/exercise" };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={back}
        title="Rhythmic Dictation"
        subhead="Hear a rhythm in meter context — mark the beat positions where notes fall."
      />
      <ModeSwitcherBar current={mode} exercisePath="/exercise/rhythmic-dictation" />
      <ExerciseLessonLink
        nodeIds={["rhythmic-dictation-simple", "rhythmic-dictation-compound", "rhythmic-dictation-syncopation"]}
      />

      <div className="space-y-5 mb-10">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">Difficulty</p>
          <div className="space-y-2">
            {DIFFICULTY_OPTIONS.map((opt) => {
              const active = difficulty === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDifficulty(opt.value)}
                  className={`w-full text-left px-4 py-3 border transition-colors ${
                    active ? "text-paper" : "bg-paper text-ink border-ink/20 hover:border-ink"
                  }`}
                  style={active ? { backgroundColor: modeColor, borderColor: modeColor } : undefined}
                  aria-pressed={active}
                >
                  <span className="block font-mono text-sm font-medium">{opt.label}</span>
                  <span className={`block font-mono text-[10px] mt-0.5 ${active ? "text-paper/70" : "text-inkFaded"}`}>
                    {opt.desc}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">Exercises per session</p>
          <div className="flex items-center gap-3 flex-wrap">
            {SESSION_LENGTH_PRESETS.map((n) => {
              const active = !showCustom && sessionLength === n;
              return (
                <button
                  key={n}
                  type="button"
                  aria-pressed={active}
                  onClick={() => { setSessionLength(n); setShowCustom(false); }}
                  className={`px-4 py-2 text-sm border transition-colors ${active ? "" : "bg-paper text-ink border-ink/20 hover:border-ink"}`}
                  style={active ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
                >
                  {n}
                </button>
              );
            })}
            <button
              type="button"
              aria-pressed={showCustom}
              onClick={() => setShowCustom(true)}
              className={`px-4 py-2 text-sm border transition-colors ${showCustom ? "" : "bg-paper text-ink border-ink/20 hover:border-ink"}`}
              style={showCustom ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
            >
              Other…
            </button>
            {showCustom && (
              <input
                type="number"
                min={1}
                max={50}
                value={sessionLength}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 50) setSessionLength(v);
                }}
                className="w-16 px-3 py-2 text-sm border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
                aria-label="Custom session length"
                autoFocus
              />
            )}
          </div>
        </div>
      </div>

      {stats && (
        <div className="mb-8 border border-ink/10">
          <div className="grid grid-cols-[1fr_5rem_5rem] px-4 py-2 border-b border-ink/10">
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Config</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Played</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Avg %</span>
          </div>
          <div className="grid grid-cols-[1fr_5rem_5rem] px-4 py-2.5">
            <span className="font-mono text-xs text-ink capitalize">{difficulty}</span>
            <span className="font-mono text-xs text-ink text-right tabular-nums">{stats.played}</span>
            <span className="font-mono text-xs text-inkFaded text-right tabular-nums">{stats.avg}%</span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => onStart({ difficulty, sessionLength })}
        className={`mt-4 ${buttonClasses("primary")}`}
        style={{ backgroundColor: modeColor }}
      >
        Start →
      </button>
    </div>
  );
}

// ─── Drill loop ────────────────────────────────────────────────────────────────

interface DrillLoopProps {
  state: DrillState;
  onChangeGrid: (g: boolean[]) => void;
  onSubmit: () => void;
  onNext: () => void;
  onReplay: () => void;
  onReplayTeach: () => void;
  onStop: () => void;
  listenMode: boolean;
  practiceMode: boolean;
  modeColor: string;
  from?: string;
}

function DrillLoop({
  state,
  onChangeGrid,
  onSubmit,
  onNext,
  onReplay,
  onReplayTeach,
  onStop,
  listenMode,
  practiceMode,
  modeColor,
  from,
}: DrillLoopProps): ReactNode {
  const { phase, round, roundNumber, config, grid, grading } = state;

  const isPlaying  = phase === "playing";
  const isAwaiting = phase === "awaiting";
  const isGraded   = phase === "graded";
  const isReveal   = phase === "reveal";
  const isLastRound = roundNumber >= config.sessionLength;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      {/* Back nav */}
      <div className="flex items-center gap-3 mb-4">
        {from === "learn" ? (
          <>
            <Link to="/learn" onClick={onStop} className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors">← Learn</Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        ) : (
          <>
            <Link to="/exercise" onClick={onStop} className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors">← Ear Training</Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        <button type="button" onClick={onStop} className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors">← Settings</button>
      </div>

      <ModeSwitcherBar
        current={practiceMode ? "review" : listenMode ? "listen" : "practice"}
      />

      <div className="flex items-baseline justify-between mb-1">
        <h1 className="font-serif text-4xl font-medium text-ink">Rhythmic Dictation</h1>
        <p className="font-mono text-xs text-inkFaded tabular-nums">
          {roundNumber} of {config.sessionLength}
        </p>
      </div>
      <p className="font-mono text-xs text-inkFaded mb-4 capitalize">
        {config.difficulty}
        {round && ` · ♩ = ${round.tempo} · ${round.params.meter}`}
      </p>

      <div
        className="w-full h-0.5 bg-ink/10 mb-6"
        role="progressbar"
        aria-valuenow={roundNumber}
        aria-valuemin={1}
        aria-valuemax={config.sessionLength}
      >
        <div
          className="h-full transition-all duration-300"
          style={{ width: `${(roundNumber / config.sessionLength) * 100}%`, backgroundColor: modeColor }}
        />
      </div>

      {/* Prompt */}
      <div className="mb-4 min-h-[2.5rem] flex items-center">
        {isPlaying && (
          <p className="font-serif italic text-xl text-inkSoft" aria-live="polite">
            Listen to the rhythm…
          </p>
        )}
        {isAwaiting && (
          <p className="font-serif italic text-xl text-ink" aria-live="polite">
            Mark the beat positions where you heard notes.
          </p>
        )}
        {isReveal && round && (
          <p className="font-serif italic text-xl text-ink" aria-live="polite">
            The rhythm was:
          </p>
        )}
        {isGraded && grading && (
          <div aria-live="polite">
            <p className="font-serif italic text-ink">
              {grading.correct
                ? "Exact match — every cell correct."
                : grading.wrongCount === 1
                  ? "One cell off."
                  : `${grading.wrongCount} cells wrong.`}
            </p>
            <p className="font-mono text-[10px] mt-1" style={{ color: practiceMode ? modeColor : "#9A9390" }}>
              {practiceMode ? "Counts toward Today." : "No schedule changes."}
            </p>
          </div>
        )}
      </div>

      {/* Correct pattern reveal (after wrong or reveal mode) */}
      {(isReveal || (isGraded && grading && !grading.correct)) && round && (
        <div className="mb-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded mb-1.5">
            {isReveal ? "Pattern" : "Expected"}
          </p>
          <p className="font-mono text-xs text-ink">{round.params.figure}</p>
          <p className="font-mono text-[10px] text-inkFaded mt-0.5">
            {round.params.pattern.split("").map((c, i) => (
              <span key={i} className={c === "1" ? "text-ink font-bold" : "text-ink/30"}>
                {c === "1" ? "●" : "○"}
              </span>
            ))}
          </p>
        </div>
      )}

      {/* Grid input — awaiting and graded phases */}
      {(isAwaiting || isGraded) && round && (
        <div className="mb-6">
          {isGraded && (
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded mb-1.5">
              Your answer
            </p>
          )}
          <RhythmicDictationInput
            meter={round.params.meter}
            grid={grid}
            onChange={onChangeGrid}
            cellStatuses={grading?.cellStatuses ?? undefined}
            disabled={isGraded}
          />
        </div>
      )}

      {/* Submit */}
      <div className="mb-6 flex gap-3 flex-wrap items-center">
        <button
          type="button"
          onClick={onSubmit}
          disabled={!isAwaiting}
          className={buttonClasses("primary")}
          style={{ backgroundColor: modeColor }}
        >
          Submit →
        </button>
        {isAwaiting && (
          <span className="font-mono text-[10px] text-inkFaded">Enter to submit · R to replay</span>
        )}
      </div>

      {/* Navigation */}
      <div className="flex gap-3 flex-wrap">
        <button
          type="button"
          disabled={!isGraded && !isReveal}
          onClick={onNext}
          className={buttonClasses("primary")}
          style={{ backgroundColor: modeColor }}
        >
          {isLastRound ? "Finish →" : "Next →"}
        </button>
        <button
          type="button"
          disabled={isPlaying}
          onClick={onReplay}
          className={buttonClasses("secondary")}
          aria-label="Replay the rhythm"
        >
          ♩ Replay
        </button>
        <button
          type="button"
          disabled={!isGraded && !isReveal}
          onClick={onReplayTeach}
          className={buttonClasses("secondary")}
          aria-label="Replay at slow tempo"
        >
          ♩♩ Slow
        </button>
      </div>

      {(isGraded || isReveal) && (
        <p className="mt-2 font-mono text-[10px] text-inkFaded">
          {isLastRound ? "Space/Enter to finish" : "Space/Enter for next"} · R to replay
        </p>
      )}
      {listenMode && <ListenAutoAdvanceControl isReveal={isReveal} onAdvance={onNext} />}
    </div>
  );
}

// ─── Summary ──────────────────────────────────────────────────────────────────

function Summary({
  config,
  accuracySum,
  roundsGraded,
  onAgain,
  onStop,
  from,
}: {
  config: RhythmicDictationExerciseConfig;
  accuracySum: number;
  roundsGraded: number;
  onAgain: () => void;
  onStop: () => void;
  from?: string;
}): ReactNode {
  const avgAccuracy = roundsGraded > 0 ? Math.round(accuracySum / roundsGraded) : null;
  const back = from === "learn"
    ? { label: "← Learn", href: "/learn" }
    : { label: "← Ear Training", href: "/exercise" };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader back={back} eyebrow="rhythmic dictation" title="Session complete" />
      <div className="grid grid-cols-2 border border-ink/10 mb-8">
        <div className="p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Exercises</p>
          <p className="font-serif text-3xl font-medium text-ink">{config.sessionLength}</p>
        </div>
        {avgAccuracy !== null && (
          <div className="p-6 border-l border-ink/10">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Avg accuracy</p>
            <p className="font-serif text-3xl font-medium text-ink">
              {avgAccuracy}<span className="text-lg text-inkSoft font-serif italic ml-0.5">%</span>
            </p>
          </div>
        )}
      </div>
      <p className="font-mono text-xs text-inkFaded mb-6 capitalize">{config.difficulty}</p>
      <div className="flex gap-3 flex-wrap items-center">
        <button type="button" onClick={onAgain} className={buttonClasses("primary")}>
          Drill again →
        </button>
        <button type="button" onClick={onStop} className={buttonClasses("secondary")}>
          Change settings
        </button>
        <Link to="/exercise" className={buttonClasses("secondary")}>All drills</Link>
      </div>
    </div>
  );
}
